<template>
  <v-dialog
    v-model="dialog"
    max-width="1040px"
    :fullscreen="$vuetify.breakpoint.smAndDown"
    scrollable
  >
    <v-card class="force-stop-card">
      <div class="force-stop-header primary white--text">
        <div class="force-stop-header__icon">
          <v-icon color="white">
            mdi-stop-circle-outline
          </v-icon>
        </div>
        <div class="force-stop-header__text">
          <div class="force-stop-header__title">
            Force Stop App
          </div>
          <div class="force-stop-header__subtitle">
            Send a force stop command to the selected device<span v-if="devices.length !== 1">s</span> and track how each device responded.
          </div>
        </div>
        <v-btn
          icon
          dark
          class="force-stop-header__close"
          @click="dialog = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="force-stop-body">
        <v-container class="pa-0">
          <!-- Trigger -->
          <div class="force-stop-section pa-5 mb-4">
            <div class="force-stop-section-title mb-4">
              Trigger force stop
            </div>

            <v-alert
              dense
              text
              type="info"
              class="mb-4"
            >
              Pick a whitelisted company app to force stop on
              {{ devices.length }} selected device<span v-if="devices.length !== 1">s</span>.
              Offline devices are queued and the command fires when they reconnect.
            </v-alert>

            <v-alert
              v-if="appsError"
              dense
              text
              type="warning"
              class="mb-4"
            >
              {{ appsError }}
            </v-alert>

            <command-target-selector
              :mode="target.mode"
              :group-id="target.groupId"
              :groups="deviceGroups"
              :device-count="devices.length"
              :loading="loadingDeviceGroups"
              :error="target.error"
              @update:mode="target.mode = $event; target.error = ''"
              @update:groupId="target.groupId = $event; target.error = ''"
            />
            <v-divider class="mb-4" />

            <v-row dense>
              <v-col
                cols="12"
                md="7"
              >
                <v-select
                  v-model="selectedPackage"
                  :items="appOptions"
                  item-text="displayLabel"
                  item-value="packageName"
                  label="App to force stop"
                  :loading="loadingApps"
                  :disabled="loadingApps || submitting"
                  no-data-text="No whitelisted apps found"
                  hide-details="auto"
                />
              </v-col>
              <v-col
                cols="12"
                md="5"
              >
                <v-text-field
                  v-model="reason"
                  label="Reason"
                  placeholder="Policy"
                  :disabled="submitting"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12">
                <v-divider class="my-3" />
                <command-expiry-picker
                  v-model="expiry"
                  :default-info="forceStopExpiryInfo"
                  :disabled="submitting"
                />
              </v-col>
            </v-row>

            <div class="force-stop-devices mt-4">
              <div
                v-for="device in devices"
                :key="deviceKey(device)"
                class="force-stop-device"
              >
                {{ deviceLabel(device) }}
              </div>
            </div>

            <div class="d-flex justify-end mt-4">
              <app-btn
                :disabled="!selectedPackage || loadingApps || submitting || !devices.length"
                :loading="submitting"
                @click="submitForceStop"
              >
                <v-icon left>
                  mdi-stop-circle-outline
                </v-icon>
                Force Stop
              </app-btn>
            </div>

            <div
              v-if="triggerResults.length"
              class="force-stop-results mt-4"
            >
              <div
                v-for="result in triggerResults"
                :key="result.key"
                class="force-stop-result"
              >
                <v-icon
                  small
                  :color="result.failed ? 'error' : (result.queued ? 'orange' : 'success')"
                  class="mr-2"
                >
                  {{ result.failed ? 'mdi-alert-circle' : (result.queued ? 'mdi-clock-outline' : 'mdi-check-circle') }}
                </v-icon>
                <span class="force-stop-result__device">{{ result.label }}</span>
                <span class="force-stop-result__text">
                  <template v-if="result.failed">{{ result.message }}</template>
                  <template v-else-if="result.queued">Queued - will fire when the device reconnects.</template>
                  <template v-else>Command sent.</template>
                </span>
                <span
                  v-if="result.eventId"
                  class="force-stop-mono"
                >Event #{{ result.eventId }}</span>
              </div>
            </div>
          </div>

          <!-- Status -->
          <div class="force-stop-section pa-5">
            <div class="force-stop-status-head mb-4">
              <div class="force-stop-section-title">
                Force stop events
              </div>
              <v-btn
                small
                text
                color="primary"
                :loading="loadingStatus"
                @click="loadStatuses"
              >
                <v-icon
                  small
                  left
                >
                  mdi-refresh
                </v-icon>
                Refresh
              </v-btn>
            </div>

            <v-row dense>
              <v-col
                cols="12"
                md="7"
              >
                <v-combobox
                  v-model="statusPackageFilter"
                  :items="packageNames"
                  label="Filter by package (optional)"
                  placeholder="All packages"
                  :disabled="loadingStatus"
                  clearable
                  hide-details="auto"
                  @change="loadStatuses"
                />
              </v-col>
            </v-row>

            <v-progress-linear
              v-if="loadingStatus"
              indeterminate
              color="primary"
              class="mt-4"
            />

            <v-alert
              v-if="!loadingStatus && statusError"
              dense
              text
              type="warning"
              class="mt-4 mb-0"
            >
              {{ statusError }}
            </v-alert>

            <v-alert
              v-if="!loadingStatus && !statusError && !statusReports.length"
              dense
              text
              type="info"
              class="mt-4 mb-0"
            >
              No force stop events recorded for the selected device<span v-if="devices.length !== 1">s</span>.
            </v-alert>

            <div
              v-for="report in statusReports"
              :key="report.key"
              class="force-stop-report mt-4"
            >
              <div class="force-stop-report__head">
                <div class="force-stop-report__main">
                  <v-icon
                    color="primary"
                    class="mr-3"
                  >
                    mdi-cellphone
                  </v-icon>
                  <div>
                    <div class="force-stop-report__name">
                      {{ report.name }}
                    </div>
                    <div class="force-stop-mono">
                      {{ report.macAddress || '-' }}
                    </div>
                  </div>
                </div>
                <div class="force-stop-report__meta">
                  <v-chip
                    small
                    outlined
                    color="primary"
                  >
                    {{ report.total }} event{{ report.total === 1 ? '' : 's' }}
                  </v-chip>
                  <v-chip
                    v-if="report.pendingCount"
                    small
                    color="orange"
                    text-color="white"
                  >
                    {{ report.pendingCount }} pending
                  </v-chip>
                </div>
              </div>

              <v-divider />

              <v-alert
                v-if="report.error"
                dense
                text
                type="warning"
                class="ma-3"
              >
                {{ report.error }}
              </v-alert>

              <v-data-table
                v-else
                :headers="eventHeaders"
                :items="report.events"
                :items-per-page="5"
                item-key="key"
                show-expand
                single-expand
                :footer-props="{ 'items-per-page-options': [5, 10, 25] }"
                class="force-stop-event-table"
                no-data-text="No force stop events for this device."
              >
                <template v-slot:[`item.packageName`]="{ item }">
                  <span class="force-stop-mono">{{ item.packageName || '-' }}</span>
                </template>
                <template v-slot:[`item.status`]="{ item }">
                  <v-chip
                    small
                    :color="statusColor(item.status)"
                    text-color="white"
                  >
                    {{ item.status || 'Unknown' }}
                  </v-chip>
                </template>
                <template v-slot:[`item.sentAt`]="{ item }">
                  <span class="force-stop-nowrap">{{ shortDateTime(item.sentAt) }}</span>
                </template>
                <template v-slot:[`item.acknowledged`]="{ item }">
                  <v-icon
                    small
                    :color="item.acknowledged ? 'success' : 'grey lighten-1'"
                    :title="item.acknowledged ? 'Acknowledged by device' : 'Not acknowledged yet'"
                  >
                    {{ item.acknowledged ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                  </v-icon>
                </template>
                <template v-slot:[`item.receivedAt`]="{ item }">
                  <span class="force-stop-nowrap">{{ shortDateTime(item.receivedAt) }}</span>
                </template>
                <template v-slot:expanded-item="{ headers, item }">
                  <td
                    :colspan="headers.length"
                    class="force-stop-detail"
                  >
                    <div class="force-stop-detail-grid">
                      <div class="force-stop-detail-row">
                        <span class="force-stop-detail__k">Event id</span>
                        <span class="force-stop-detail__v force-stop-mono">{{ item.eventId || '-' }}</span>
                      </div>
                      <div class="force-stop-detail-row">
                        <span class="force-stop-detail__k">Package</span>
                        <span class="force-stop-detail__v force-stop-mono">{{ item.packageName || '-' }}</span>
                      </div>
                      <div class="force-stop-detail-row">
                        <span class="force-stop-detail__k">Sent at</span>
                        <span class="force-stop-detail__v">{{ shortDateTime(item.sentAt) }}</span>
                      </div>
                      <div class="force-stop-detail-row">
                        <span class="force-stop-detail__k">Received at</span>
                        <span class="force-stop-detail__v">{{ shortDateTime(item.receivedAt) }}</span>
                      </div>
                      <div
                        v-if="item.sendBy"
                        class="force-stop-detail-row force-stop-detail-row--full"
                      >
                        <span class="force-stop-detail__k">Requested by</span>
                        <span class="force-stop-detail__v force-stop-mono">{{ item.sendBy }}</span>
                      </div>
                      <div
                        v-if="item.deviceMessage"
                        class="force-stop-detail-row force-stop-detail-row--full"
                      >
                        <span class="force-stop-detail__k">Device message</span>
                        <span class="force-stop-detail__v">{{ item.deviceMessage }}</span>
                      </div>
                      <div
                        v-for="payload in item.payloads"
                        :key="payload.label"
                        class="force-stop-detail-row force-stop-detail-row--full"
                      >
                        <span class="force-stop-detail__k">{{ payload.label }}</span>
                        <pre class="force-stop-payload">{{ payload.value }}</pre>
                      </div>
                    </div>
                  </td>
                </template>
              </v-data-table>
            </div>
          </div>
        </v-container>
      </v-card-text>

      <v-divider />
      <v-card-actions class="force-stop-actions px-4 py-3">
        <v-spacer />
        <v-btn
          text
          @click="dialog = false"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import moment from 'moment'
  import CommandTargetSelector from '../CommandTargetSelector.vue'
  import CommandExpiryPicker from '../CommandExpiryPicker.vue'
  import { buildTtlMinutes } from '@/util/commandCatalog'
  import groupCommandTarget from '@/mixins/groupCommandTarget'

  const toastOptions = { position: 'topRight' }

  // Payload fields the status endpoint may expose under either casing.
  const PAYLOAD_FIELDS = [
    ['Sent payload', ['payload', 'rawPayload', 'requestPayload', 'sentPayload']],
    ['Device payload', ['devicePayload', 'responsePayload', 'rawResponse', 'deviceRawPayload']],
  ]

  export default {
    name: 'ForceStopAppDialog',
    components: {
      CommandTargetSelector,
      CommandExpiryPicker,
    },
    mixins: [groupCommandTarget],
    props: {
      value: {
        type: Boolean,
        default: false,
      },
      selectedCompanyId: {
        type: [Number, String],
        default: 0,
      },
      devices: {
        type: Array,
        default: () => [],
      },
    },
    data () {
      return {
        target: { mode: 'devices', groupId: null, error: '' },
        expiry: { mode: 'default', ttlMinutes: null, expiresAt: null },
        dialog: this.value,
        loadingApps: false,
        appsError: '',
        appOptions: [],
        selectedPackage: null,
        reason: 'Policy',
        submitting: false,
        triggerResults: [],
        loadingStatus: false,
        statusError: '',
        statusPackageFilter: null,
        statusReports: [],
        eventHeaders: [
          { text: 'Package', value: 'packageName' },
          { text: 'Status', value: 'status' },
          { text: 'Sent', value: 'sentAt' },
          { text: 'Acked', value: 'acknowledged', align: 'center', sortable: false },
          { text: 'Received', value: 'receivedAt' },
          { text: '', value: 'data-table-expand' },
        ],
      }
    },
    computed: {
      forceStopExpiryInfo () {
        const catalog = this.groupCommandCatalog
        return (catalog && catalog.expiryByType && catalog.expiryByType.ForceStopApp) || null
      },
      packageNames () {
        return this.appOptions.map(app => app.packageName)
      },
    },
    watch: {
      value (val) {
        this.dialog = val
        if (val) {
          this.onOpen()
        }
      },
      dialog (val) {
        this.$emit('input', val)
      },
    },
    methods: {
      onOpen () {
        this.target = {
          mode: this.devices.length ? 'devices' : 'group',
          groupId: null,
          error: '',
        }
        this.expiry = { mode: 'default', ttlMinutes: null, expiresAt: null }
        this.loadDeviceGroups()
        this.loadGroupCommandCatalog()
        this.appsError = ''
        this.selectedPackage = null
        this.reason = 'Policy'
        this.triggerResults = []
        this.statusError = ''
        this.statusPackageFilter = null
        this.statusReports = []
        this.loadApps()
        this.loadStatuses()
      },
      // Reads a value that the API may return as camelCase or PascalCase.
      pick (source, ...keys) {
        for (const key of keys) {
          const pascal = key.charAt(0).toUpperCase() + key.slice(1)
          if (source?.[key] !== undefined && source?.[key] !== null) {
            return source[key]
          }
          if (source?.[pascal] !== undefined && source?.[pascal] !== null) {
            return source[pascal]
          }
        }
        return undefined
      },
      hasNumericId (value) {
        return Number.isFinite(Number(value)) && Number(value) > 0
      },
      deviceNumericId (device) {
        const id = this.pick(device, 'id', 'deviceId')
        return this.hasNumericId(id) ? Number(id) : 0
      },
      deviceMac (device) {
        return String(this.pick(device, 'macAddress', 'deviceMacAddress') || '').trim()
      },
      deviceKey (device) {
        return String(this.deviceNumericId(device) || this.deviceMac(device) || Math.random())
      },
      deviceLabel (device) {
        const name = device?.name || device?.deviceModel || 'Unnamed device'
        const mac = this.deviceMac(device)
        return mac ? `${name} - ${mac}` : name
      },
      async loadApps () {
        if (!this.selectedCompanyId) {
          this.appOptions = []
          this.appsError = 'Select a company to load its whitelisted apps.'
          return
        }

        this.loadingApps = true
        this.appsError = ''
        try {
          const response = await this.$http.post(`/CompanyKioskApp/paginated/${this.selectedCompanyId}`, {
            pageNumber: 1,
            pageSize: 500,
            search: '',
          })

          const items = response.data?.data?.items || []
          const seen = new Set()

          this.appOptions = items
            .map((app) => {
              const packageName = String(app?.packageName || '').trim()
              const title = String(app?.title || app?.label || packageName).trim()
              return {
                packageName,
                displayLabel: packageName && title !== packageName
                  ? `${title} (${packageName})`
                  : (title || packageName),
              }
            })
            .filter((app) => {
              if (!app.packageName || seen.has(app.packageName.toLowerCase())) {
                return false
              }
              seen.add(app.packageName.toLowerCase())
              return true
            })

          if (!this.appOptions.length) {
            this.appsError = 'No whitelisted apps found for this company.'
          }
        } catch (error) {
          this.appOptions = []
          this.appsError = 'Could not load whitelisted apps for this company.'
        } finally {
          this.loadingApps = false
        }
      },
      buildTriggerBody (device) {
        const body = {
          packageName: this.selectedPackage,
          reason: String(this.reason || '').trim() || 'Policy',
        }

        const ttlMinutes = buildTtlMinutes(this.expiry)
        if (ttlMinutes) {
          body.ttlMinutes = ttlMinutes
        }
        const deviceId = this.deviceNumericId(device)

        // The endpoint accepts either identifier; prefer the numeric device id.
        if (deviceId) {
          body.deviceId = deviceId
        } else {
          body.macAddress = this.deviceMac(device)
        }

        return body
      },
      async submitForceStop () {
        if (!this.selectedPackage) {
          this.$toast.error('Select an app to force stop.', 'Error', toastOptions)
          return
        }

        if (this.target.mode === 'group') {
          if (!this.target.groupId) {
            this.target.error = 'Select a device group'
            this.$toast.error('Select a device group.', 'Error', toastOptions)
            return
          }

          this.submitting = true
          try {
            await this.sendGroupCommand(
              this.target.groupId,
              'ForceStopApp',
              {
                packageName: this.selectedPackage,
                reason: String(this.reason || '').trim() || 'Policy',
              },
              this.expiry,
            )
          } finally {
            this.submitting = false
          }
          return
        }

        if (!this.devices.length) {
          this.$toast.error('Select at least one device.', 'Error', toastOptions)
          return
        }

        const unidentified = this.devices.find(
          device => !this.deviceNumericId(device) && !this.deviceMac(device),
        )
        if (unidentified) {
          this.$toast.error('One or more selected devices have no device id or MAC address.', 'Error', toastOptions)
          return
        }

        this.submitting = true
        try {
          const results = await Promise.allSettled(
            this.devices.map(device => this.$http.post('Message/ForceStopApp', this.buildTriggerBody(device))),
          )

          this.triggerResults = results.map((result, index) => {
            const device = this.devices[index]
            const base = { key: this.deviceKey(device), label: this.deviceLabel(device) }

            if (result.status === 'rejected') {
              return {
                ...base,
                failed: true,
                message: result.reason?.response?.data?.message ||
                  result.reason?.message ||
                  'Force stop failed.',
              }
            }

            const data = result.value?.data
            const payload = this.pick(data, 'data') || data
            const failed = data?.success === false || data?.isSuccess === false

            return {
              ...base,
              failed,
              message: this.pick(data, 'message', 'error') || 'Force stop failed.',
              eventId: this.pick(payload, 'eventId') || '',
              queued: this.pick(payload, 'queued') === true,
            }
          })

          const failedCount = this.triggerResults.filter(result => result.failed).length
          const successCount = this.triggerResults.length - failedCount
          const queuedCount = this.triggerResults.filter(result => !result.failed && result.queued).length

          if (successCount > 0) {
            const queuedNote = queuedCount ? ` ${queuedCount} queued until the device reconnects.` : ''
            this.$toast.success(
              `Force stop sent to ${successCount} device${successCount === 1 ? '' : 's'}.${queuedNote}`,
              'Success',
              toastOptions,
            )
          }

          if (failedCount > 0) {
            const firstFailure = this.triggerResults.find(result => result.failed)
            this.$toast.error(
              firstFailure?.message || 'Some force stop commands failed.',
              'Error',
              toastOptions,
            )
          }

          // Show the freshly created events without the user hunting for Refresh.
          await this.loadStatuses()
        } finally {
          this.submitting = false
        }
      },
      async loadStatuses () {
        if (!this.devices.length) {
          this.statusReports = []
          return
        }

        this.loadingStatus = true
        this.statusError = ''

        const packageName = String(this.statusPackageFilter || '').trim()

        try {
          const reports = await Promise.all(
            this.devices.map(async (device) => {
              const base = {
                key: this.deviceKey(device),
                name: this.deviceLabel(device),
                macAddress: this.deviceMac(device),
                total: 0,
                pendingCount: 0,
                events: [],
                error: '',
              }
              const deviceId = this.deviceNumericId(device)

              if (!deviceId) {
                // The status endpoint is keyed by the numeric device id only.
                return { ...base, error: 'This device has no numeric id, so its force stop events cannot be listed.' }
              }

              try {
                const response = await this.$http.get(
                  `Message/GetForceStopAppStatus/${encodeURIComponent(deviceId)}`,
                  packageName ? { params: { packageName } } : undefined,
                )
                return { ...base, ...this.normalizeStatus(response.data) }
              } catch (error) {
                return {
                  ...base,
                  error: error?.response?.data?.message ||
                    error?.message ||
                    'Could not load force stop events for this device.',
                }
              }
            }),
          )

          this.statusReports = reports
        } catch (error) {
          this.statusReports = []
          this.statusError = error?.response?.data?.message ||
            error?.message ||
            'Could not load force stop statuses.'
        } finally {
          this.loadingStatus = false
        }
      },
      normalizeStatus (raw) {
        const root = (raw && !Array.isArray(raw) && this.pick(raw, 'data')) || raw
        const list = Array.isArray(root)
          ? root
          : (this.pick(root, 'commands', 'items', 'events', 'statuses', 'results') || [])

        const events = (Array.isArray(list) ? list : [])
          .filter(event => event && typeof event === 'object')
          .map((event, index) => {
            const eventId = this.pick(event, 'eventId', 'id') || ''
            return {
              key: `${eventId || 'event'}-${index}`,
              eventId,
              packageName: this.pick(event, 'packageName') || '',
              sentAt: this.pick(event, 'sentAt') || '',
              acknowledged: this.pick(event, 'acknowledged') === true,
              receivedAt: this.pick(event, 'receivedAt') || '',
              status: this.pick(event, 'status') || '',
              deviceMessage: this.pick(event, 'deviceMessage', 'message') || '',
              sendBy: this.pick(event, 'sendBy', 'sentBy', 'requestedBy') || '',
              payloads: this.buildPayloads(event),
            }
          })

        const macAddress = String(this.pick(root, 'macAddress', 'deviceMacAddress') || '')

        return {
          events,
          total: Number(this.pick(root, 'total', 'totalCount') ?? events.length) || events.length,
          pendingCount: Number(this.pick(root, 'pendingCount') ?? 0) || 0,
          ...(macAddress ? { macAddress } : {}),
        }
      },
      buildPayloads (event) {
        return PAYLOAD_FIELDS
          .map(([label, keys]) => ({ label, value: this.formatPayload(this.pick(event, ...keys)) }))
          .filter(payload => payload.value)
      },
      formatPayload (value) {
        if (value === undefined || value === null || value === '') {
          return ''
        }

        if (typeof value === 'object') {
          return JSON.stringify(value, null, 2)
        }

        // Payloads usually arrive as JSON strings; pretty-print when parseable.
        try {
          return JSON.stringify(JSON.parse(String(value)), null, 2)
        } catch (error) {
          return String(value)
        }
      },
      shortDateTime (value) {
        if (!value) {
          return '-'
        }
        // Backend sends naive UTC timestamps; render them in local time.
        const parsed = moment.utc(value)
        return parsed.isValid() && parsed.year() > 2010
          ? parsed.local().format('DD MMM YYYY, h:mm a')
          : '-'
      },
      statusColor (status) {
        switch (String(status || '').trim().toLowerCase()) {
          case 'stopped':
          case 'success':
          case 'completed':
          case 'delivered':
            return 'success'
          case 'pending':
          case 'queued':
            return 'orange'
          case 'failed':
          case 'error':
          case 'notfound':
          case 'not found':
            return 'error'
          default:
            return 'grey'
        }
      },
    },
  }
</script>

<style scoped>
  .force-stop-card {
    display: flex;
    flex-direction: column;
    max-height: 88vh;
    overflow: hidden;
  }

  .v-dialog--fullscreen .force-stop-card {
    max-height: 100vh;
    height: 100vh;
  }

  .force-stop-header {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 16px;
  }

  .force-stop-header__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    flex: 0 0 auto;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.18);
  }

  .force-stop-header__icon >>> .v-icon {
    font-size: 28px;
  }

  .force-stop-header__text {
    min-width: 0;
    flex: 1 1 auto;
  }

  .force-stop-header__title {
    font-size: 1.35rem;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: normal;
    text-transform: none;
  }

  .force-stop-header__subtitle {
    color: rgba(255, 255, 255, 0.88);
    font-size: 0.9rem;
    line-height: 1.35;
    letter-spacing: normal;
    text-transform: none;
    white-space: normal;
    margin-top: 3px;
  }

  .force-stop-header__close {
    flex: 0 0 auto;
    align-self: flex-start;
  }

  .force-stop-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    background: #fafafa;
    padding-top: 20px;
  }

  .force-stop-section {
    background: #ffffff;
    border: 1px solid #ececec;
    border-radius: 12px;
  }

  .force-stop-section-title {
    font-size: 1.15rem;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.85);
  }

  .force-stop-status-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
  }

  .force-stop-devices {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .force-stop-device {
    font-size: 0.85rem;
    padding: 3px 10px;
    border-radius: 999px;
    background: #f1f5f4;
    color: rgba(0, 0, 0, 0.72);
  }

  .force-stop-results {
    border-top: 1px solid #ececec;
    padding-top: 12px;
  }

  .force-stop-result {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px 10px;
    font-size: 0.9rem;
    padding: 3px 0;
  }

  .force-stop-result__device {
    font-weight: 600;
    color: rgba(0, 0, 0, 0.82);
  }

  .force-stop-result__text {
    color: rgba(0, 0, 0, 0.66);
  }

  .force-stop-report {
    border: 1px solid #ececec;
    border-radius: 12px;
    overflow: hidden;
  }

  .force-stop-report__head {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    padding: 14px 16px;
  }

  .force-stop-report__main {
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;
  }

  .force-stop-report__name {
    font-size: 1.05rem;
    font-weight: 600;
  }

  .force-stop-report__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .force-stop-mono {
    font-family: 'Roboto Mono', monospace;
    font-size: 0.8rem;
    color: rgba(0, 0, 0, 0.55);
  }

  .force-stop-nowrap {
    white-space: nowrap;
    font-size: 0.85rem;
    color: rgba(0, 0, 0, 0.7);
  }

  .force-stop-event-table >>> tbody td {
    font-size: 0.95rem;
    height: 52px;
  }

  .force-stop-event-table >>> thead th,
  .force-stop-event-table >>> tbody td {
    white-space: nowrap;
  }

  .force-stop-detail {
    background: #f7f9fc;
    padding: 14px 20px !important;
  }

  .force-stop-detail-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px 24px;
  }

  .force-stop-detail-row {
    display: flex;
    flex-direction: column;
  }

  .force-stop-detail-row--full {
    grid-column: 1 / -1;
  }

  .force-stop-detail__k {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: rgba(0, 0, 0, 0.5);
  }

  .force-stop-detail__v {
    font-size: 0.92rem;
    color: rgba(0, 0, 0, 0.82);
    word-break: break-word;
  }

  .force-stop-payload {
    margin: 2px 0 0;
    padding: 8px 10px;
    border-radius: 8px;
    background: #eef2f7;
    font-family: 'Roboto Mono', monospace;
    font-size: 0.78rem;
    color: rgba(0, 0, 0, 0.75);
    white-space: pre-wrap;
    word-break: break-word;
    max-height: 220px;
    overflow: auto;
  }

  .force-stop-actions {
    flex: 0 0 auto;
  }
</style>
